"use client";

import { useEffect, useRef, useState } from "react";
import type { FileAttachment } from "@ui/components/core/PromptIsland";

/** Only one reference image is accepted — the legacy generate path takes a single source image. */
const MAX_FILES = 1;

/** A {@link FileAttachment} that also keeps its raw File, so live generation can upload it. */
interface LocalAttachment extends FileAttachment {
  file: File;
}

/**
 * Local image attachment state for the InteractiveImage create mode.
 *
 * Keeps only `image/*` files, capped at {@link MAX_FILES}, and revokes every object URL on
 * remove / reset / unmount so nothing leaks. `rawFiles` exposes the underlying Files to upload.
 */
export function useImageAttachments() {
  const [files, setFiles] = useState<LocalAttachment[]>([]);
  // Mirror of files so the unmount cleanup revokes the latest URLs without a stale closure.
  const filesRef = useRef<LocalAttachment[]>([]);
  filesRef.current = files;

  const add = (incoming: File[]) => {
    const images = incoming.filter(file => file.type.startsWith("image/"));
    if (images.length === 0) return;
    setFiles(prev => {
      const room = MAX_FILES - prev.length;
      if (room <= 0) return prev;
      const next = images.slice(0, room).map(file => ({
        name: file.name,
        size: file.size,
        previewUrl: URL.createObjectURL(file),
        file,
      }));
      return [...prev, ...next];
    });
  };

  const remove = (index: number) => {
    setFiles(prev => {
      const target = prev[index];
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const reset = () => {
    setFiles(prev => {
      prev.forEach(file => URL.revokeObjectURL(file.previewUrl));
      return prev.length === 0 ? prev : [];
    });
  };

  // Revoke any outstanding URLs when the component unmounts.
  useEffect(
    () => () => {
      filesRef.current.forEach(file => URL.revokeObjectURL(file.previewUrl));
    },
    [],
  );

  const rawFiles = files.map(f => f.file);

  return { files, rawFiles, add, remove, reset, maxFiles: MAX_FILES };
}
